import { Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import {
  ConfirmDialog,
  HistoryTimeline,
  InlineAlert,
  Loader,
  LoadingButton,
  SourceBadge,
  StudentRecordForm,
} from "src/grading/components";
import KeyValueCard from "src/grading/components/KeyValueCard/KeyValueCard";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { HistoryPageArgs } from "src/models/history";
import { useAppDispatch } from "src/store/hooks";
import {
  useDeactivateStudentMutation,
  useGetStudentHistoryQuery,
  useGetStudentQuery,
} from "src/store/reducers/classes/classesApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";

/** A student record with only the allowed fields, consent flags, and its History. */
const StudentPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { studentId } = useParams<{ studentId: string }>();
  const { hasPermissions } = usePermissions();
  const {
    data: student,
    isLoading,
    isError,
    refetch,
  } = useGetStudentQuery(studentId);
  const canAudit = hasPermissions(
    permissions.READ_AUDIT.action,
    permissions.READ_AUDIT.subject,
  );
  const useHistoryPage = (page: Required<HistoryPageArgs>) =>
    useGetStudentHistoryQuery({ studentId, ...page });
  const [deactivate, { isLoading: deactivating }] =
    useDeactivateStudentMutation();
  // Admins edit through students:school, teachers through students:class; the server decides
  // which fields each may change and reports them as readOnlyFields (R7, R9).
  const canEdit =
    hasPermissions(
      permissions.UPDATE_SCHOOL_STUDENTS.action,
      permissions.UPDATE_SCHOOL_STUDENTS.subject,
    ) ||
    hasPermissions(
      permissions.UPDATE_CLASS_STUDENTS.action,
      permissions.UPDATE_CLASS_STUDENTS.subject,
    );

  const [error, setError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const fullName = student
    ? `${student.firstName} ${student.lastName}`
    : undefined;
  useEffect(() => {
    if (fullName) document.title = `${fullName} · ${t("app.name")}`;
  }, [fullName, t]);

  const studentName = student
    ? `${student.lastName}, ${student.firstName}`
    : undefined;
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({
        label: t("tabs.classes"),
        path: "/grading/classes",
      }),
    );
    if (studentName) dispatch(addBreadcrumb({ label: studentName }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, studentName, t]);

  const remove = async () => {
    setConfirmingRemove(false);
    try {
      await deactivate({ studentId }).unwrap();
      history.goBack();
    } catch (e) {
      const code = refusalCode(e);
      setError(
        code
          ? refusalCopy(t, code)
          : messageOf(e, t("student.deactivateError")),
      );
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !student)
    return (
      <InlineAlert severity="error">
        {t("student.notFound", {
          defaultValue: "Student not found or outside your scope.",
        })}
      </InlineAlert>
    );

  const ageRow = student.birthDate
    ? { label: t("student.birthDate"), value: student.birthDate }
    : { label: t("classes.age"), value: student.ageYears ?? "—" };
  const canDeactivate =
    canEdit && student.active && student.allowedActions.deactivate;

  return (
    <>
      <PageHeader
        title={`${student.lastName}, ${student.firstName}`}
        eyebrow={
          student.gradeLevel
            ? t("classes.gradeLabel", {
                grade: student.gradeLevel,
                defaultValue: "Grade {{grade}}",
              })
            : undefined
        }
        description={
          student.active
            ? undefined
            : t("classes.inactive", { defaultValue: "Inactive" })
        }
      />
      <Stack spacing={3}>
        <KeyValueCard
          title={t("classes.studentCard", { defaultValue: "Student" })}
          rows={[
            {
              label: t("classes.benchmarkTable"),
              value: student.benchmarkSex
                ? t(`classes.sex${student.benchmarkSex}`, {
                    defaultValue: student.benchmarkSex,
                  })
                : t("review.notSet"),
            },
            ageRow,
            { label: t("classes.sisId"), value: student.externalId ?? "—" },
            {
              label: t("classes.sourceLabel", { defaultValue: "Source" }),
              value: "",
              chip: <SourceBadge source={student.source} />,
            },
          ]}
        />

        <Paper
          sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}
          component="section"
          aria-labelledby="student-record"
        >
          <Typography id="student-record" variant="h4" component="h2">
            {t("student.recordTitle", { defaultValue: "Record" })}
          </Typography>
          <StudentRecordForm
            student={student}
            readOnly={!canEdit}
            onReload={() => refetch()}
          />
          {error && <InlineAlert severity="error">{error}</InlineAlert>}
          {canDeactivate && (
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              <LoadingButton
                color="error"
                variant="outlined"
                loading={deactivating}
                onClick={() => setConfirmingRemove(true)}
                label={t("student.deactivate")}
              />
            </Stack>
          )}
        </Paper>

        {canAudit && (
          <Paper
            sx={{ p: 3 }}
            component="section"
            aria-labelledby="student-history"
          >
            <Typography
              id="student-history"
              variant="h4"
              component="h2"
              gutterBottom
            >
              {t("history.title")}
            </Typography>
            <HistoryTimeline
              subjectName={`${student.firstName} ${student.lastName}`}
              usePage={useHistoryPage}
            />
          </Paper>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmingRemove}
        tone="destructive"
        title={t("student.deactivateTitle", {
          defaultValue: "Deactivate this student?",
        })}
        body={t("student.deactivateBody", {
          defaultValue:
            "Finalized results are kept; the student leaves every class roster.",
        })}
        confirmLabel={t("student.deactivate", { defaultValue: "Deactivate" })}
        loading={deactivating}
        onConfirm={remove}
        onClose={() => setConfirmingRemove(false)}
      />
    </>
  );
};

export default StudentPage;
